import React, { useState } from 'react';
import {
    Play,
    Shield,
    AlertTriangle,
    Check,
    BookOpen,
    ChevronUp,
    ChevronDown,
    Lightbulb
} from 'lucide-react';

const FoodAllergensIntro = ({ onStart }) => {
    const [isDefinitionsOpen, setIsDefinitionsOpen] = useState(true);

    const allergens = [
        { name: 'Milk', desc: 'Dairy ingredients' },
        { name: 'Eggs', desc: 'Chicken eggs' },
        { name: 'Fish', desc: 'Finfish varieties' },
        { name: 'Shellfish', desc: 'Crustaceans/mollusks' },
        { name: 'Tree Nuts', desc: 'Almonds, walnuts, etc.' },
        { name: 'Peanuts', desc: 'Peanut products' },
        { name: 'Wheat', desc: 'Wheat ingredients' },
        { name: 'Soy', desc: 'Soy products' },
        { name: 'Sesame', desc: 'Seeds/oil/paste' },
    ];

    return (
        <div className="min-h-screen bg-[#fcf7ec] py-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-900 flex flex-col items-center">
            <div className="max-w-4xl w-full bg-white rounded-3xl shadow-sm p-8 sm:p-10 relative">
                <div className="flex justify-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-1.5 rounded-full text-sm font-medium text-gray-600">
                        <div className="bg-gray-800 rounded-full p-0.5">
                            <Play size={10} className="text-white fill-current" />
                        </div>
                        Chapter intro
                    </div>
                </div>

                {/* Title & Description */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        Allergens and cross-contact
                    </h1>
                    <p className="text-gray-600 text-lg leading-relaxed max-w-xl mx-auto">
                        Before you begin, review the major allergens and how cross-contact works
                        so you can tag dishes accurately and protect guests.
                    </p>
                </div>

                {/* Purple Section - 9 Major Allergens */}
                <div className="bg-[#FDF4FF] rounded-2xl p-6 mb-6 border border-purple-100">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="bg-[#9F2B68] p-2 rounded-lg">
                            <Shield className="text-white w-5 h-5" />
                        </div>
                        <span className="bg-[#9F2B68] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                            The 9 major allergens
                        </span>
                    </div>

                    <p className="text-gray-900 text-left font-medium mb-6 ml-1">
                        Select these only if they are <span className="font-bold">PRESENT</span> in the dish.
                    </p>

                    <div className="grid grid-cols-1 text-left sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {allergens.map((allergen) => (
                            <div key={allergen.name} className="bg-white p-4 rounded-xl border border-purple-50 shadow-sm">
                                <h3 className="font-bold text-gray-900 text-lg mb-1">{allergen.name}</h3>
                                <p className="text-gray-500 text-sm">{allergen.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Orange Section - Cross-contact */}
                <div className="bg-[#FFF7ED] rounded-2xl p-6 mb-8 border border-orange-100">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-[#F97316] p-2 rounded-lg">
                            <AlertTriangle className="text-white w-5 h-5" />
                        </div>
                        <span className="bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                            Cross-contact
                        </span>
                    </div>

                    <h3 className="font-bold text-left text-gray-900 mb-2 ml-1">
                        Possible exposure during prep (shared equipment, oil, or surfaces).
                    </h3>
                    <p className="text-gray-600 text-left text-sm leading-relaxed ml-1">
                        If there is a realistic chance an allergen could contact the dish during preparation,
                        mark cross-contact as Yes—even if the allergen is not an ingredient.
                    </p>
                </div>

                {/* Key Reminders */}
                <div className="bg-[#F8FAFC] rounded-2xl p-6 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-yellow-500 text-xl"><Lightbulb /></span>
                        <h3 className="font-bold text-gray-900">Key reminders</h3>
                    </div>
                    <ul className="space-y-3">
                        {[
                            "Tag only what is in the dish; use cross-contact for prep risks.",
                            "When unsure, check the recipe or ask a manager/chef.",
                            "Accurate allergen tagging builds guest trust and reduces liability."
                        ].map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                                <Check className="text-green-500 w-5 h-5 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 text-sm font-medium">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Quick Definitions Accordion */}
                <div className="border border-gray-200 rounded-2xl overflow-hidden mb-12">
                    <div
                        className="w-full flex items-center justify-between p-4 bg-white  transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <BookOpen className="text-gray-900 w-5 h-5" />
                            <span className="font-bold text-gray-900">Quick definitions</span>
                        </div>
                    </div>

                    {/* {isDefinitionsOpen && ( */}
                        <div className="p-4 pt-0 bg-white border-t text-left border-gray-100">
                            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
                                <span className="font-bold text-gray-900 text-sm">Allergen:</span>
                                <span className="text-gray-600 text-sm">An ingredient that can cause an allergic reaction in some guests.</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 sm:gap-4 py-3">
                                <span className="font-bold text-gray-900 text-sm">Cross-contact:</span>
                                <span className="text-gray-600 text-sm">When a safe food touches an allergen during prep, cooking, or service (e.g., shared fryer oil or cutting boards).</span>
                            </div>
                        </div>
                    {/* // )} */}
                </div>

                {/* Start Button */}
                <div className="flex justify-center">
                    <button
                        onClick={onStart}
                        className="bg-[#A01B3F] hover:bg-[#8a1736] text-white font-bold py-3 px-10 rounded-xl"
                    >
                        Start
                    </button>
                </div>

            </div>
        </div>
    );
};

export default FoodAllergensIntro;
